import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Link,
  VStack,
  Alert,
  AlertIcon,
  Badge,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import HeaderMenus from './HeaderMenus';
import { callApi } from '../callApi';
import { AppContext } from '../components/AppContext';

const PayerResponsesDashboard = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { accessToken } = useContext(AppContext);
  const [showFailed, setShowFailed] = useState(false);

  useEffect(() => {
    const fetchPayerResponses = async () => {
      if (!accessToken) {
        setLoading(false);
        setError("Authentication token not found. Please log in.");
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await callApi('/payer-responses', accessToken);
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch data');
        }

        const data = await response.json();
        if (data && data.status === 'success') {
          setCases(data.cases);
        } else {
          throw new Error(data.message || 'Failed to fetch data');
        }
      } catch (err) {
        setError(`An error occurred while fetching the appeals data: ${err.message}`);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPayerResponses();
  }, [accessToken]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'SUCCESS':
        return <Badge colorScheme="green">Success</Badge>;
      case 'FAILED_NO_CONTENT':
        return <Badge colorScheme="red">Failed: No Content</Badge>;
      default:
        return <Badge colorScheme="gray">Unknown</Badge>;
    }
  };

  const filteredCases = showFailed ? cases : cases.filter(c => c.last_appeal_status !== 'FAILED_NO_CONTENT');

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={10}>
        <VStack spacing={6} align="stretch">
          <Heading as="h1" size="xl">
            Appeals Dashboard
          </Heading>
          <Text>This dashboard shows all cases with one or more generated appeal letters.</Text>

          {loading && (
            <Box textAlign="center" p={10}>
              <Spinner size="xl" />
              <Text mt={4}>Loading Appeals...</Text>
            </Box>
          )}

          {error && (
            <Alert status="error">
              <AlertIcon />
              {error}
            </Alert>
          )}

          {!loading && !error && (
            <>
              <Box>
                <Button onClick={() => setShowFailed(!showFailed)} size="sm">
                  {showFailed ? 'Hide Failed Appeals' : 'Show Failed Appeals'}
                </Button>
              </Box>
              <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
                <Table variant="simple">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Patient ID</Th>
                      <Th>Case Description</Th>
                      <Th>Payer</Th>
                      <Th>Carrier</Th>
                      <Th>State</Th>
                      <Th>Last Appeal Date</Th>
                      <Th>Last Appeal Status</Th>
                      <Th>Case ID</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredCases.length > 0 ? (
                      filteredCases.map((caseItem) => (
                        <Tr key={caseItem.external_case_id}>
                          <Td>
                            <Link as={RouterLink} to={`/case-appeals/${caseItem.external_case_id}`} color="blue.500" fontWeight="bold">
                              {caseItem.external_patient_id}
                            </Link>
                          </Td>
                          <Td title={caseItem.case_description}>
                            {caseItem.case_description?.substring(0, 50) + (caseItem.case_description?.length > 50 ? '...' : '')}
                          </Td>
                          <Td>{caseItem.payer}</Td>
                          <Td>{caseItem.carrier}</Td>
                          <Td>{caseItem.state}</Td>
                          <Td>{formatDate(caseItem.last_appeal_date)}</Td>
                          <Td>{renderStatusBadge(caseItem.last_appeal_status)}</Td>
                          <Td>{caseItem.external_case_id}</Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={8} textAlign="center">
                          No cases with appeals found.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            </>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default PayerResponsesDashboard;